import { logoImage } from '@quickbit/qb-design-system';
import { jsPDF } from 'jspdf';
import { useCallback, useState } from 'react';
import { NewCorporateWalletTransaction } from 'api/walletApi.types';
import { useOrganisation } from 'contexts';

export interface Args {
  transaction: NewCorporateWalletTransaction;
  recipient?: { label: string };
  authorizedBy?: { email: string };
}

export const useGenerateReceiptPDF = () => {
  const {
    selectedMerchant: { name: merchantName },
  } = useOrganisation();
  const [error, setError] = useState(false);

  const generateReceiptPDF = useCallback(
    ({ transaction, authorizedBy, recipient }: Args) => {
      try {
        const { amount, createdAt, destination, currency } = transaction;

        //PDF generation starts here
        const pdf = new jsPDF();
        const createdAtDate = new Date(createdAt);
        let sectionStart = 0;

        pdf.addImage({
          imageData: logoImage,
          x: 150,
          y: 11,
          width: 50,
          height: 12,
          format: 'WEBP',
          compression: 'FAST',
        });
        pdf.setFontSize(22);
        pdf.text('Payment Settlement Details', 10, 19);
        pdf.setFontSize(10);
        pdf.text(
          `Date: ${new Date().toLocaleString('default', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`,
          10,
          25
        );
        pdf.line(9, 27, 202, 27);

        //Sender, Reciever details section

        // Headings
        sectionStart = 35;
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('Sender Name:', 10, sectionStart);
        pdf.text('Authorised By:', 10, sectionStart + 4);
        pdf.text('Receiver Name:', 110, sectionStart, { align: 'left' });
        pdf.text('Receiving Address:', 110, sectionStart + 4, {
          align: 'left',
        });

        // Split the address into multiple lines if it is too long
        const address = destination
          ? (destination.address.match(/.{1,27}/g) ?? destination.address)
          : '-';

        // Values
        pdf.setFont(pdf.getFont().fontName, 'normal');
        pdf.text(merchantName, 37, sectionStart);
        pdf.text(authorizedBy?.email ?? '', 37, sectionStart + 4);
        pdf.text(recipient?.label || ' - ', 145, sectionStart, {
          align: 'left',
        });
        pdf.text(address, 145, sectionStart + 4, { align: 'left' });

        // Horizontal line
        pdf.line(105, sectionStart - 5, 105, sectionStart + 10);
        //Section break line
        pdf.line(9, sectionStart + 13, 202, sectionStart + 13);

        sectionStart = 60;
        pdf.setFontSize(10);
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('Transaction Amount:', 10, sectionStart - 7);
        pdf.text('Transaction Date:', 10, sectionStart);
        pdf.text('Blockchain Transaction ID:', 10, sectionStart + 6);

        pdf.setFont(pdf.getFont().fontName, 'normal');
        pdf.text(`${amount} ${currency}`, 50, sectionStart - 7);
        pdf.text(createdAtDate.toLocaleString(), 42, sectionStart);
        pdf.text(
          destination?.blockchainTransactionId ?? '-',
          58,
          sectionStart + 6
        );
        pdf.line(9, sectionStart + 8, 202, sectionStart + 8);

        //This will download the pdf automatically
        pdf.save(
          `${merchantName.replaceAll(' ', '_')}-${
            recipient?.label.replaceAll(' ', '_') || ''
          }-${createdAtDate.toLocaleDateString()}.pdf`
        );
      } catch (e) {
        console.error(e);
        setError(true);
      }
    },
    [merchantName]
  );

  return { generateReceiptPDF, error, setError };
};
